/**
 * RBAC capabilities context (Phase 1, P1-3).
 *
 * Reads the user's capabilities from the Auth0 id-token claim
 * `https://calterminal.com/capabilities` (injected by the Auth0 post-login Action) and
 * exposes `can(cap)`.
 *
 * IMPORTANT — mirrors the backend `RBAC_ENFORCED` kill switch (RBAC_PLAN.md §13.A):
 * `RBAC_ENFORCED` is FALSE during Phase 1, so `can()` returns TRUE for everything and the
 * UI is completely unchanged (nothing hidden/disabled). Phase 2 flips this to true (and the
 * backend flag) once capabilities are set on all real users. The backend is always
 * authoritative; this is UX only.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

const CAP_CLAIM = 'https://calterminal.com/capabilities';
const STANDARD: string[] = ['view_dashboards', 'use_cal']; // fail-closed default (least privilege)

// Frontend enforcement flag — keep in lockstep with the backend `RBAC_ENFORCED` env.
// GO-LIVE (Aug 24): enforcement ON. Roll back by setting this to false + backend env to false.
const RBAC_ENFORCED = true;

interface CapabilitiesContextType {
  capabilities: string[];
  can: (capability: string) => boolean;
  enforced: boolean; // mirror of RBAC_ENFORCED, so screens can fall back to legacy gates when off
  loading: boolean;
}

const CapabilitiesContext = createContext<CapabilitiesContextType>({
  capabilities: [],
  can: () => true,
  enforced: RBAC_ENFORCED,
  loading: true,
});

export const CapabilitiesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getIdTokenClaims, isAuthenticated } = useAuth0();
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (!isAuthenticated) {
          if (active) { setCapabilities([]); setLoading(false); }
          return;
        }
        const claims = await getIdTokenClaims();
        const raw = claims ? (claims as any)[CAP_CLAIM] : undefined;
        const caps = Array.isArray(raw) && raw.length ? (raw as string[]) : STANDARD;
        if (active) setCapabilities(caps);
      } catch {
        if (active) setCapabilities(STANDARD);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [getIdTokenClaims, isAuthenticated]);

  // Phase 1: NOT enforced → always true (UI unchanged). Phase 2: real capability check.
  const can = useCallback(
    (capability: string) => (RBAC_ENFORCED ? capabilities.includes(capability) : true),
    [capabilities]
  );

  return (
    <CapabilitiesContext.Provider value={{ capabilities, can, enforced: RBAC_ENFORCED, loading }}>
      {children}
    </CapabilitiesContext.Provider>
  );
};

export const useCapabilities = () => useContext(CapabilitiesContext);
